import React, { useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useParams, useNavigate } from 'react-router-dom';
import { useMediaQuery } from '@mantine/hooks';
import {
  Box,
  Button,
  Container,
  Stack,
  Text,
  Image,
  Loader,
  useMantineTheme,
  Grid,
  Group,
  Modal,
  ActionIcon
} from '@mantine/core';
import { IconArrowLeft, IconShoppingCart, IconX, IconCube } from '@tabler/icons-react';
import DOMPurify from 'dompurify';

import { useStore } from '../../state';
import { CartController } from '../cart/CartController';
import { REACT_APP_DISCORD_INVITE } from '../store/StoreController';
import { Product } from '@common/merch';
import { THEME_COLORS } from '../../theme/ThemeConstants';
import { formatPrice } from '../../utilities/Currency';
import { Footer } from '../../components/layout/Footer';
import { ModelViewer } from '../../components/model/ModelViewer';
import { TopNavigation } from '../../components/navigation/TopNavigation';
import { MobileHeader } from '../../components/layout/MobileHeader';
import { HeroBackdrop } from '../../components/background/HeroBackdrop';
import { LAYOUT_CONSTANTS, Z_INDEX } from '../../components/layout/LayoutConstants';
import axios from 'axios';
import './ProductDetailsView.scss';

// Ensure any links in sanitized product descriptions open safely in a new tab.
DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  if (node.tagName === 'A') {
    node.setAttribute('target', '_blank');
    node.setAttribute('rel', 'noopener noreferrer');
  }
});

const PRODUCT_DESCRIPTION_SANITIZE_CONFIG = {
  ALLOWED_TAGS: ['p', 'br', 'strong', 'b', 'em', 'i', 'u', 'ul', 'ol', 'li', 'a', 'h1', 'h2', 'h3', 'h4', 'span'],
  ALLOWED_ATTR: ['href', 'target', 'rel']
};

const HAIRLINE = 'rgba(255, 255, 255, 0.09)';

// Subtle fill behind still-image thumbnails / image products (no frame on the main stage).
const STAGE_SURFACE = 'rgba(255, 255, 255, 0.025)';

// A single gallery item — either the product's 3D model or a still image.
type MediaItem = { kind: 'model' } | { kind: 'image'; url: string; altText?: string };

export const ProductDetailsView: React.FC = observer(() => {
  const { handle } = useParams<{ handle: string }>();
  const navigate = useNavigate();
  const rootStore = useStore();
  const cartController = useMemo(() => new CartController(rootStore), [rootStore]);
  const theme = useMantineTheme();
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.md})`);

  const [product, setProduct] = useState<Product | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedVariantId, setSelectedVariantId] = useState<string | null>(null);
  const [selectedMediaIndex, setSelectedMediaIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  useEffect(() => {
    const loadProduct = async () => {
      if (!handle) return;

      setIsLoading(true);
      try {
        const response = await axios.get(`/api/merch/product/${handle}`);
        if (response.data.success) {
          const productData = response.data.data;
          setProduct(productData);
          if (productData.variants?.[0]) {
            setSelectedVariantId(productData.variants[0].id);
          }
        } else {
          rootStore.alertStore.showAlert('Product not found', 'error');
          navigate('/');
        }
      } catch (error) {
        console.error('Failed to load product:', error);
        rootStore.alertStore.showAlert('Failed to load product', 'error');
        navigate('/');
      } finally {
        setIsLoading(false);
      }
    };

    loadProduct();
  }, [handle, rootStore, navigate]);

  const handleAddToCart = async () => {
    if (!product || !selectedVariantId) return;

    const success = await cartController.addToCart(selectedVariantId);
    if (success) {
      rootStore.alertStore.showAlert('Added to cart!', 'success');
      rootStore.applicationStore.openCart();
    }
  };

  const renderChrome = () =>
    isMobile ? <MobileHeader isOpen={false} onToggle={() => navigate('/')} /> : <TopNavigation transparent />;

  if (isLoading) {
    return (
      <>
        <HeroBackdrop imageUrl='/assets/images/hero.png' />
        {renderChrome()}
        <Box
          style={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            zIndex: 2
          }}
        >
          <Loader size='lg' color={THEME_COLORS.accent.primary} />
        </Box>
      </>
    );
  }

  if (!product) return null;

  const images = product.images || [];
  const selectedVariant = product.variants?.find((v) => v.id === selectedVariantId) || product.variants?.[0];
  const price = selectedVariant ? parseFloat(selectedVariant.price.amount) : 0;
  const currency = selectedVariant?.price.currencyCode || 'USD';
  const hasVariants = product.variants && product.variants.length > 1;
  const available = product.availableForSale;
  const model = product.model3d;
  const eyebrow = product.collection?.title || 'TrappedMC Merch';

  // One unified gallery: the 3D model (if any) leads, followed by the images.
  const mediaItems: MediaItem[] = [
    ...(model ? [{ kind: 'model' as const }] : []),
    ...images.map((image) => ({ kind: 'image' as const, url: image.url, altText: image.altText }))
  ];
  const selectedMedia = mediaItems[selectedMediaIndex] ?? mediaItems[0];

  const soldOutNotice = (mode: 'desktop' | 'mobile') => (
    <Box
      style={{
        ...(mode === 'mobile'
          ? { flex: 1, display: 'flex' }
          : { minWidth: 210, paddingLeft: 32, paddingRight: 32, display: 'inline-flex' }),
        height: 46,
        alignItems: 'center',
        justifyContent: 'center',
        border: `1px solid ${HAIRLINE}`,
        borderRadius: 3,
        cursor: 'default'
      }}
    >
      <Text size='sm' fw={700} c={THEME_COLORS.text.secondary} style={{ letterSpacing: '0.01em' }}>
        Sold Out
      </Text>
    </Box>
  );

  const addToCartButton = (mode: 'desktop' | 'mobile') =>
    !available ? (
      soldOutNotice(mode)
    ) : (
      <Button
        leftSection={<IconShoppingCart size={16} />}
        variant='filled'
        style={{
          ...(mode === 'mobile' ? { flex: 1 } : { minWidth: 210, paddingLeft: 32, paddingRight: 32 }),
          backgroundColor: available ? THEME_COLORS.accent.orange : '#242424',
          color: available ? '#000000' : '#ffffff',
          height: 46,
          fontSize: 14,
          fontWeight: 700,
          letterSpacing: '0.01em',
          borderRadius: 3,
          boxShadow: 'none',
          transition: 'background-color 0.1s ease-in-out'
        }}
        onMouseEnter={(e) => {
          if (available) e.currentTarget.style.backgroundColor = THEME_COLORS.accent.hover;
        }}
        onMouseLeave={(e) => {
          if (available) e.currentTarget.style.backgroundColor = THEME_COLORS.accent.orange;
        }}
        onMouseDown={(e) => {
          if (available) e.currentTarget.style.backgroundColor = THEME_COLORS.accent.active;
        }}
        onMouseUp={(e) => {
          if (available) e.currentTarget.style.backgroundColor = THEME_COLORS.accent.hover;
        }}
        onClick={handleAddToCart}
        loading={rootStore.cartStore.isLoading}
        disabled={!selectedVariantId || !available}
      >
        Add to Cart
      </Button>
    );

  // ── Media gallery ───────────────────────────────────────────────────────────
  const mediaGallery = (
    <Stack gap={12}>
      <Box
        className='product-stage'
        style={{
          position: 'relative',
          width: '100%',
          aspectRatio: '1 / 1',
          borderRadius: 12,
          overflow: 'hidden'
        }}
      >
        {selectedMedia?.kind === 'model' && model ? (
          <ModelViewer
            glbUrl={model.glbUrl}
            usdzUrl={model.usdzUrl}
            posterUrl={model.posterUrl}
            cameraDistance={model.cameraDistance}
            alt={model.alt || product.title}
          />
        ) : selectedMedia?.kind === 'image' ? (
          <Box onClick={() => setLightboxOpen(true)} style={{ width: '100%', height: '100%', cursor: 'zoom-in' }}>
            <Image
              className='product-stage-image'
              src={selectedMedia.url}
              alt={selectedMedia.altText || product.title}
              fit='cover'
              h='100%'
              w='100%'
            />
          </Box>
        ) : (
          <Box
            style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <Text size='sm' c={THEME_COLORS.text.dimmed}>
              No image available
            </Text>
          </Box>
        )}
      </Box>

      {mediaItems.length > 1 && (
        <Group gap={8}>
          {mediaItems.map((item, index) => {
            const active = selectedMediaIndex === index;
            return (
              <Box
                key={index}
                onClick={() => setSelectedMediaIndex(index)}
                style={{
                  position: 'relative',
                  width: 64,
                  height: 64,
                  borderRadius: 6,
                  overflow: 'hidden',
                  backgroundColor: STAGE_SURFACE,
                  border: active ? `1px solid ${THEME_COLORS.accent.orange}` : `1px solid ${HAIRLINE}`,
                  boxShadow: active ? `inset 0 0 0 1px ${THEME_COLORS.accent.orange}` : 'none',
                  opacity: active ? 1 : 0.6,
                  cursor: 'pointer',
                  transition: 'opacity 0.15s ease-in-out, border-color 0.15s ease-in-out'
                }}
                onMouseEnter={(e) => {
                  if (!active) e.currentTarget.style.opacity = '0.9';
                }}
                onMouseLeave={(e) => {
                  if (!active) e.currentTarget.style.opacity = '0.6';
                }}
              >
                {item.kind === 'model' ? (
                  <>
                    {model?.posterUrl && (
                      <Image src={model.posterUrl} alt={model.alt || product.title} fit='cover' h='100%' w='100%' />
                    )}
                    <Box
                      style={{
                        position: 'absolute',
                        left: 4,
                        bottom: 4,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 3,
                        padding: '2px 5px',
                        borderRadius: 4,
                        backgroundColor: 'rgba(0, 0, 0, 0.65)'
                      }}
                    >
                      <IconCube size={10} color={THEME_COLORS.accent.orange} />
                      <Text size='8px' fw={700} c='white' style={{ letterSpacing: '0.04em' }}>
                        3D
                      </Text>
                    </Box>
                  </>
                ) : (
                  <Image src={item.url} alt={item.altText || product.title} fit='cover' h='100%' w='100%' />
                )}
              </Box>
            );
          })}
        </Group>
      )}
    </Stack>
  );

  // ── Product info ──────────────────────────────────────────────────────────
  const productInfo = (
    <Box style={{ maxWidth: 480 }}>
      <Text size='xs' fw={600} c={THEME_COLORS.accent.orange} tt='uppercase' style={{ letterSpacing: '0.08em' }}>
        {eyebrow}
      </Text>

      <Text
        c='white'
        fw={800}
        mt={8}
        style={{ fontSize: isMobile ? '1.5rem' : '2rem', lineHeight: 1.18, letterSpacing: '-0.02em' }}
      >
        {product.title}
      </Text>

      <Text
        c={available ? 'white' : THEME_COLORS.text.dimmed}
        fw={600}
        mt={12}
        style={{ fontSize: '1.25rem', fontVariantNumeric: 'tabular-nums' }}
      >
        {formatPrice(price, currency)}
      </Text>

      {hasVariants && (
        <Stack gap={8} mt={32}>
          <Text size='xs' fw={700} c={THEME_COLORS.text.dimmed} tt='uppercase' style={{ letterSpacing: '0.14em' }}>
            Variant
          </Text>
          <Group gap={8}>
            {product.variants.map((variant) => {
              const isSelected = variant.id === selectedVariantId;
              return (
                <Box
                  key={variant.id}
                  onClick={() => setSelectedVariantId(variant.id)}
                  style={{
                    padding: '8px 12px',
                    borderRadius: 3,
                    backgroundColor: isSelected ? THEME_COLORS.accent.tint : THEME_COLORS.surface.neutralFill,
                    border: `1px solid ${isSelected ? THEME_COLORS.accent.borderTint : HAIRLINE}`,
                    cursor: 'pointer',
                    transition: 'border-color 0.15s ease-in-out, background-color 0.15s ease-in-out'
                  }}
                  onMouseEnter={(e) => {
                    if (!isSelected) e.currentTarget.style.borderColor = THEME_COLORS.border.hairlineHover;
                  }}
                  onMouseLeave={(e) => {
                    if (!isSelected) e.currentTarget.style.borderColor = HAIRLINE;
                  }}
                >
                  <Text size='sm' fw={600} c={isSelected ? 'white' : THEME_COLORS.text.secondary}>
                    {variant.title}
                  </Text>
                </Box>
              );
            })}
          </Group>
        </Stack>
      )}

      {!isMobile && <Box mt={32}>{addToCartButton('desktop')}</Box>}

      {!available && (
        <Text size='sm' c={THEME_COLORS.text.secondary} mt={12}>
          Restocks are announced in our{' '}
          <Text
            component='a'
            href={REACT_APP_DISCORD_INVITE}
            target='_blank'
            rel='noopener noreferrer'
            inherit
            c={THEME_COLORS.accent.orange}
            style={{ textDecoration: 'none' }}
          >
            Discord
          </Text>
          .
        </Text>
      )}

      {(product.descriptionHtml || product.description) && (
        <Box mt={32} pt={32} style={{ borderTop: `1px solid ${HAIRLINE}` }}>
          <Text
            size='xs'
            fw={700}
            c={THEME_COLORS.text.dimmed}
            tt='uppercase'
            mb={12}
            style={{ letterSpacing: '0.14em' }}
          >
            Details
          </Text>
          <Box
            className='product-description'
            style={{ color: THEME_COLORS.text.secondary, lineHeight: 1.7, fontSize: 14 }}
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(
                product.descriptionHtml || product.description,
                PRODUCT_DESCRIPTION_SANITIZE_CONFIG
              )
            }}
          />
        </Box>
      )}
    </Box>
  );

  return (
    <>
      <HeroBackdrop imageUrl='/assets/images/hero.png' />
      {renderChrome()}
      <Box style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', position: 'relative', zIndex: 2 }}>
        <Container
          size={1240}
          style={{
            paddingTop: isMobile ? LAYOUT_CONSTANTS.MOBILE_HEADER_HEIGHT + 20 : 32,
            paddingBottom: isMobile ? LAYOUT_CONSTANTS.MOBILE_CART_HEIGHT + 40 : 72,
            flex: 1,
            width: '100%'
          }}
        >
          <Box
            onClick={() => navigate('/')}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = '#ffffff';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = THEME_COLORS.text.secondary;
            }}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              width: 'fit-content',
              color: THEME_COLORS.text.secondary,
              cursor: 'pointer',
              transition: 'color 0.15s ease',
              marginBottom: isMobile ? 24 : 32
            }}
          >
            <IconArrowLeft size={16} />
            <Text size='xs' fw={500} c='inherit' tt='uppercase' style={{ letterSpacing: '0.06em' }}>
              Back to store
            </Text>
          </Box>

          <Grid gutter={{ base: 24, md: 64 }} align='stretch'>
            <Grid.Col span={{ base: 12, md: 7 }}>
              <Box style={{ position: isMobile ? undefined : 'sticky', top: isMobile ? undefined : 80 }}>
                {mediaGallery}
              </Box>
            </Grid.Col>

            <Grid.Col span={{ base: 12, md: 5 }}>{productInfo}</Grid.Col>
          </Grid>
        </Container>

        <Box style={{ marginBottom: isMobile ? LAYOUT_CONSTANTS.MOBILE_CART_HEIGHT : 0 }}>
          <Footer />
        </Box>
      </Box>

      {isMobile && (
        <Box
          style={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: Z_INDEX.SIDEBAR,
            backgroundColor: 'rgba(15, 15, 15, 0.85)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            borderTop: `1px solid ${HAIRLINE}`,
            padding: '12px 16px',
            display: 'flex',
            alignItems: 'center',
            gap: 16
          }}
        >
          <Box style={{ flexShrink: 0 }}>
            <Text size='9px' fw={600} c={THEME_COLORS.text.dimmed} tt='uppercase' style={{ letterSpacing: '0.06em' }}>
              Price
            </Text>
            <Text
              size='lg'
              fw={800}
              c={available ? 'white' : THEME_COLORS.text.dimmed}
              lh={1.1}
              style={{ fontVariantNumeric: 'tabular-nums' }}
            >
              {formatPrice(price, currency)}
            </Text>
          </Box>
          {addToCartButton('mobile')}
        </Box>
      )}

      <Modal
        opened={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        size='auto'
        padding={0}
        withCloseButton={false}
        styles={{ content: { backgroundColor: 'transparent', boxShadow: 'none' }, body: { padding: 0 } }}
      >
        <Box style={{ position: 'relative', maxWidth: '90vw', maxHeight: '90vh' }}>
          <ActionIcon
            variant='filled'
            size={48}
            onClick={() => setLightboxOpen(false)}
            style={{
              position: 'absolute',
              top: 16,
              right: 16,
              borderRadius: '50%',
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              color: 'white',
              zIndex: 10
            }}
          >
            <IconX size={24} />
          </ActionIcon>
          {selectedMedia?.kind === 'image' && (
            <Image
              src={selectedMedia.url}
              alt={selectedMedia.altText || product?.title}
              fit='contain'
              style={{ maxWidth: '90vw', maxHeight: '90vh' }}
            />
          )}
        </Box>
      </Modal>
    </>
  );
});
